import { FC, useRef } from "react";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../../../../../common/utils";
import PaperCard from "../../../../../components/widgets/PaperCard";
import PaperCardHeader from "../../../../../components/widgets/PaperCard/PaperCardHeader";
import { EmissionsChainTransportResultsDTO } from "../../../../../datasources/api-client/data-contracts";
import { headerCellMeasure } from "../../../utils";
import CustomNoRowsOverlay from "../../TransportGreenrouter/components/CustomNoRowsOverlay";
import { dataCol, rows } from "./data";

type Props = {
	data: EmissionsChainTransportResultsDTO | undefined;
	isLoading: boolean;
	onReload: () => void;
	title: string;
};

const EmissionChainTransport: FC<Props> = ({ data, isLoading, onReload, title }) => {
	const containerRef = useRef<HTMLDivElement | null>(null);

	const { t } = useTranslation("result.transport");

	const dataSample = data?.total || {};

	const columns = dataCol.map((col) => ({
		...col,
		headerName: t(col.headerName ?? "-"),
		renderHeader: () => (
			<Stack
				direction="row"
				sx={{
					fontWeight: "bold",
				}}
				spacing={1}
				alignItems={"baseline"}
			>
				<Typography variant="subtitle1">{t(col.headerName ?? "-")}</Typography>
				<Typography variant="body2" color="text.secondary">
					{headerCellMeasure(col, dataSample)}
				</Typography>
			</Stack>
		),
	}));

	if (isLoading) return <Skeleton variant="rounded" height="100%" width="100%" />;

	return (
		<PaperCard
			ref={containerRef}
			header={
				<PaperCardHeader containerRef={containerRef} title={title} isLoading={isLoading} onReload={onReload} />
			}
			content={
				<Stack direction="column" height={"100%"} width={"100%"}>
					<Box height={"auto"}>
						<DataGrid
							rows={data ? rows(data) : []}
							columns={columns || []}
							hideFooter
							checkboxSelection={false}
							isCellEditable={() => false}
							getRowHeight={() => 60}
							disableColumnMenu
							slots={{
								noRowsOverlay: () => <CustomNoRowsOverlay />,
							}}
						/>
					</Box>

					<Stack display={"flex"} flexDirection={"row"} p={2}>
						<Typography variant="h6" flex={1}>
							{t("total tc emissions")}
						</Typography>
						<Typography paddingLeft={4} variant="h6" flex={1}>
							{(data?.total.co2Wtw.value && formatNumber(data?.total.co2Wtw.value, "decimal", 2, 2)) || 0}
						</Typography>
					</Stack>
				</Stack>
			}
			sxContent={{ paddingRight: 0, paddingLeft: 0 }}
		/>
	);
};

export default EmissionChainTransport;
